// IMPORT REACT
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// IMPORT PACKAGES
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// IMPORT CSS
import "../../../theme/style.scss";
import "react-phone-number-input/style.css";

// IMPORT UI COMPONENTS
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import CustomBreadcrumb from "../../../components/BreadcrumbsComponent/CustomBreadcrumbs";
import SkeletonWrapper from "../../../components/Skeleton/SkeletonWrapper";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { CustomTextField } from "../../../components/CustomFields/TextField";

// IMPORT SERVICE
import { AlertType } from "../../../services/models/AlertType";
import { EncryptDecryptService } from "../../../services/common/EncryptionService";
import { PreferencesContext } from "../../../PreferenceContext";
import {
  CreateMedicineIndicationForSOSMedicine,
  GetMedicineIndicationForSOSMedicineDetail,
  UpdateMedicineIndicationForSOSMedicine,
} from "../../../services/MedicineIndicationForSOSMedicineService";
import { PAGE_SIZE } from "../../../services/common/const";
import { GetSpecialityList } from "../../../services/SpecialityService";
import { GetDepartmentList } from "../../../services/DepartmentService";
import { CustomSelect } from "../../../components/CustomFields/CustomSelect";

// SCHEMA FOR FORM

interface selectOption {
  label: string;
  value: string;
}

interface MedicineIndicationForSOSMedicineDetail {
  medicineIndicationForSOSMedicineDetailId: number;
  medicineIndicationForSOSMedicineId: number;
  medicineIndicationForSOSLanguageCode: string;
  medicineIndicationForSOSLanguage: string;
  medicineIndicationForSOSMedicineName: string;
  isDeleted: boolean;
  mode: string;
}

interface formInitialValueTypes {
  medicineIndicationForSOSMedicineId: number;
  consumerId: number;
  orgId: number;
  branchId: number;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  // medicineIndicationForSOSTypeCode: String;
  medicineIndicationForSOSMedicineDetailData: Array<MedicineIndicationForSOSMedicineDetail> | null;
  isDeleted: boolean;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  specialityId: string;
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  specialityId: "",
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};
// DEFAULT SCHEMA VALUE FOR FORM

export default function MedicineIndicationForSOSMedicineSave(Props: any) {
  const { isForPopUp = false, handleClosePopUp, name } = Props;

  // HOOKS AND CONTEXT
  const navigate = useNavigate();
  const edService = new EncryptDecryptService();
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const initMedicineIndicationForSOSMedicineData: formInitialValueTypes = {
    medicineIndicationForSOSMedicineId: 0,
    consumerId: 0,
    orgId: 0,
    branchId: 0,
    departmentId:
      userContext?.departmentId > 0
        ? {
          label: userContext?.departmentName || "",
          value: userContext?.departmentId?.toString(),
        }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
          label: userContext?.specialityName || "",
          value: userContext?.specialityId?.toString(),
        }
        : null,
    // medicineIndicationForSOSTypeCode: "Medicine_Indication_For_SOS_Medicine",
    medicineIndicationForSOSMedicineDetailData: isForPopUp
      ? [{
        medicineIndicationForSOSMedicineDetailId: 0,
        medicineIndicationForSOSMedicineId: 0,
        medicineIndicationForSOSLanguageCode: "ENGLISH-IN", // example default
        medicineIndicationForSOSLanguage: name?.toUpperCase() || "",
        medicineIndicationForSOSMedicineName: "",
        isDeleted: false,
        mode: "I",
      }]
      : [],
    isDeleted: false,
  };
  function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
  }

  // GETTTING PARAMS
  let urlParams = useQuery();
  let curRawmedicineIndicationForSOSMedicineId = urlParams.get("q");
  let curmedicineIndicationForSOSMedicineId = 0;
  if (
    curRawmedicineIndicationForSOSMedicineId !== null &&
    curRawmedicineIndicationForSOSMedicineId !== undefined &&
    !isForPopUp
  ) {
    let decId = edService.decrypt(curRawmedicineIndicationForSOSMedicineId);
    if (decId !== "" && decId !== null && decId !== undefined) {
      curmedicineIndicationForSOSMedicineId = +decId;
    }
  }

  const [param, setParam] = useState(parameters);
  const [specialityData, setSpecialityData] = useState<any[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);
  // IF NOT FIND THEN NAVIGATE TO NOT FOUND PAGE  --- PENDING -- ASK TO SIR ABOUT THIS

  // STATES -- FOR ERROR HANDLING AND LOADING
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(true);

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    medicineIndicationForSOSMedicineId: Yup.number().nullable(),
    orgId: Yup.number().required(),
    consumerId: Yup.number().required(),
    branchId: Yup.number().required(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    medicineIndicationForSOSMedicineDetailData: Yup.array()
      .of(
        Yup.object().shape({
          medicineIndicationForSOSLanguage: Yup.string().required(
            "Language is required."
          ),
          medicineIndicationForSOSLanguageCode: Yup.string().required(
            "Language Code is required."
          ),
          medicineIndicationForSOSMedicineName: Yup.string()
            .transform((value) => {
              // Convert empty string to null
              return value?.trim() === "" ? null : value;
            })
            .when("medicineIndicationForSOSLanguageCode", {
              is: (val: string) => val === "ENGLISH-IN",
              then: (schema) => schema.required("Medicine Indication For SOS is required."),
              otherwise: (schema) => schema.nullable(),
            }),
        })
      )
      .required("Required"),
    isLocked: Yup.boolean(),
    isDeleted: Yup.boolean(),
  });

  // FORM HOOK
  const methods = useForm({
    resolver: yupResolver(validationSchema), // IGNORE THIS ERROR
    defaultValues: initMedicineIndicationForSOSMedicineData, // DEFAULT VALUE -- DON'T CREATE STATE FOR IT -- STATE CAN INCREASE SITE LOAD
  });

  //DEPARTMENT LIST
  const getDepartmentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetDepartmentList(params);
      if (result?.data?.data?.departmentColl) {
        const resultData = result?.data?.data.departmentColl;
        setDepartmentData(resultData);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log("err:", e);
    }
  };

  //Speciality LIST
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setSpecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  // GET  MEDICINE GROUP DETAIL
  const getmedicineIndicationForSOSMedicineDetailById = async () => {
    let Result: any;
    let params = new URLSearchParams();
    params.append(
      "medicineIndicationForSOSMedicineId",
      curmedicineIndicationForSOSMedicineId?.toString()
    );
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());

    try {
      const res: any = await GetMedicineIndicationForSOSMedicineDetail(params);
      Result = res.data.data;
      if (curmedicineIndicationForSOSMedicineId > 0) {
        methods.reset({
          ...initMedicineIndicationForSOSMedicineData,
          medicineIndicationForSOSMedicineId:
            Result?.medicineIndicationForSOSMedicineId || 0,

          medicineIndicationForSOSMedicineDetailData:
            Result?.medicineIndicationForSOSMedicineDetailCollection,

          departmentId: Result?.departmentId
            ? {
              label: Result?.departmentName,
              value: Result?.departmentId,
            }
            : null,
          specialityId: Result?.specialityId
            ? {
              label: Result?.specialityName,
              value: Result?.specialityId,
            }
            : null,
        });
      } else {
        methods.reset({
          ...initMedicineIndicationForSOSMedicineData,
          medicineIndicationForSOSMedicineId: Result?.medicineIndicationForSOSMedicineId || 0,
          medicineIndicationForSOSMedicineDetailData: Result?.medicineIndicationForSOSMedicineDetailCollection,
          departmentId: methods.getValues()?.departmentId,
          specialityId: methods.getValues()?.specialityId,
        });
      }
      return Result;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // SAVE AND UPDATE MEDICINE Indication For SOS Medicine
  const saveMedicineIndicationForSOSMedicine = async (
    value: formInitialValueTypes
  ) => {
    try {
      let result: any = "";
      let tempmedicineIndicationForSOSMedicineDetailData: any[] = [];
      value.medicineIndicationForSOSMedicineDetailData?.forEach((x: any) => {
        tempmedicineIndicationForSOSMedicineDetailData.push({
          medicineIndicationForSOSMedicineDetailId: x.medicineIndicationForSOSMedicineDetailId,
          medicineIndicationForSOSMedicineId: x.medicineIndicationForSOSMedicineId,
          medicineIndicationForSOSLanguageCode: x.medicineIndicationForSOSLanguageCode,
          medicineIndicationForSOSMedicineName: x.medicineIndicationForSOSMedicineName,
          isDeleted: false,
          mode: x.medicineIndicationForSOSMedicineDetailId > 0 ? "U" : "I",
        });
      });

      let formData = {
        medicineIndicationForSOSMedicineId: value.medicineIndicationForSOSMedicineId,
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        branchId: userContext?.branchId,
        orgId: userContext?.organizationId,
        consumerId: userContext?.consumerId,
        // medicineIndicationForSOSTypeCode: value?.medicineIndicationForSOSTypeCode.trim(),
        DataManagedBy: userContext.loginDetail.userId,
        medicineIndicationForSOSMedicineDetailData: JSON.stringify(tempmedicineIndicationForSOSMedicineDetailData),
      };
      if (curmedicineIndicationForSOSMedicineId > 0) {
        result = await UpdateMedicineIndicationForSOSMedicine(formData);
      } else {
        result = await CreateMedicineIndicationForSOSMedicine(formData);
      }
      if (result?.data) {
        if (!isForPopUp) {
          navigate(-1);
        } else {
          if (result.data) {
            handleClosePopUp("INDICATION_FOR_SOS_CREATE", result.data);
          }
        }
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // ON SUBMIT OF FORM
  const onSubmit = async (data: any) => {
    saveMedicineIndicationForSOSMedicine(data);
  };

  // HANDLE BACK
  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  // FETCH DATA
  const fetchData = async () => {
    try {
      if (curmedicineIndicationForSOSMedicineId <= 0) {
        await getDepartmentList();
      }
      if (curmedicineIndicationForSOSMedicineId > 0) {
        await getmedicineIndicationForSOSMedicineDetailById();
      } else if (userContext?.departmentId > 0) {
        await fetchSpeciality(userContext?.departmentId?.toString());
        if (userContext?.specialityId > 0) {
          await getmedicineIndicationForSOSMedicineDetailById();
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, [curmedicineIndicationForSOSMedicineId]);

  // HANDLE DELAY FOR SKELETON
  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowSkeleton(false), 50); // Delay unmounting the Skeleton
      return () => clearTimeout(timeout);
    } else {
      setShowSkeleton(true);
    }
  }, [isLoading]);

  return (
    <Box>
      <Box className={"main-container"}>
        {isForPopUp === false && (
          <>
            <Grid container className="page-header">
              <Grid item xs={6} sm={6} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    alignItems: "center",
                  }}>
                  <CustomBreadcrumb />
                </Box>
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <Box className="page-header-icon">
                  <Button
                    color="primary"
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}>
                    Back
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)} action="">
            <Grid container gap={2}>
              <Grid item xs={12} className={"group-container"}>
                <Grid item container xs={12} spacing={2}>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.departmentId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={curmedicineIndicationForSOSMedicineId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    }
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setSpecialityData([]);
                                  }
                                }
                              }}
                              className={`text-input ${methods.formState.errors.departmentId
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors.specialityId
                          ? "validation-message"
                          : ""
                          }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isDisabled={curmedicineIndicationForSOSMedicineId > 0}
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  }
                                );
                                if (e && e?.value) {
                                  setIsLoading(true);
                                  await getmedicineIndicationForSOSMedicineDetailById();
                                  setIsLoading(false);
                                }
                              }}
                              className={`text-input ${methods.formState.errors.specialityId
                                ? "error"
                                : ""
                                }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  {/* <Grid item xs={12}>
                    <Typography variant="h6">Basic Information</Typography>
                  </Grid> */}
                  {methods
                    .getValues()
                    ?.medicineIndicationForSOSMedicineDetailData?.map(
                      (item: MedicineIndicationForSOSMedicineDetail, index: number) => (
                        <Grid item xs={12} sm={6} md={6} key={index}>
                          <FormControl fullWidth className="from-controller">
                            <FormLabel className="fileds-label-text">
                              {" "}
                              Medicine Indication for SOS medicine - {item?.medicineIndicationForSOSLanguage}
                            </FormLabel>
                            <SkeletonWrapper
                              isLoading={isLoading}
                              className="skeleton-input"
                            >
                              <CustomTextField
                                placeholder="Ex. Vaccination, Follow-up Visit, Review Reports"
                                autoCapitalize="none"
                                type="text"
                                fullWidth
                                size="small"
                                {...methods.register(
                                  `medicineIndicationForSOSMedicineDetailData.${index}.medicineIndicationForSOSMedicineName`
                                )}
                                error={
                                  !!methods.formState.errors
                                    .medicineIndicationForSOSMedicineDetailData &&
                                  methods.formState.errors
                                    .medicineIndicationForSOSMedicineDetailData[index]
                                    ?.medicineIndicationForSOSMedicineName
                                }
                                className={`text-input ${methods.formState.errors
                                  .medicineIndicationForSOSMedicineDetailData &&
                                  methods.formState.errors
                                    .medicineIndicationForSOSMedicineDetailData[index]
                                    ?.medicineIndicationForSOSMedicineName
                                  ? "error"
                                  : ""
                                  }`}
                              />

                              <Typography className="validation-message">
                                {methods.formState.errors
                                  .medicineIndicationForSOSMedicineDetailData &&
                                  methods.formState.errors
                                    .medicineIndicationForSOSMedicineDetailData[index]
                                    ?.medicineIndicationForSOSMedicineName?.message}
                              </Typography>
                            </SkeletonWrapper>
                          </FormControl>
                        </Grid>
                      )
                    )}
                  {/* <Grid item xs={12} sm={isForPopUp ? 12 : 6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${methods.formState.errors
                          .medicineIndicationForSOSMedicineName
                          ? "validation-message"
                          : ""
                          }`}>
                        Medicine Indication for SOS medicine Name *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={showSkeleton}
                        className="skeleton-input">
                        <CustomTextField
                          placeholder=""
                          autoCapitalize="none"
                          type="text"
                          fullWidth
                          autoFocus
                          size="small"
                          {...methods.register(
                            "medicineIndicationForSOSMedicineName"
                          )}
                          error={
                            !!methods.formState.errors
                              .medicineIndicationForSOSMedicineName
                          }
                          className={`text-input ${methods.formState.errors
                            .medicineIndicationForSOSMedicineName
                            ? "error"
                            : ""
                            }`}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {
                        methods.formState.errors
                          .medicineIndicationForSOSMedicineName?.message
                      }
                    </Typography>
                  </Grid> */}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "end", gap: 2 }}>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="contained"
                    type="submit"
                    className="primary-button"
                    name="save"
                    disabled={showSkeleton}>
                    {curmedicineIndicationForSOSMedicineId > 0
                      ? "Update"
                      : "Save"}
                  </LoadingButton>
                  <LoadingButton
                    color="primary"
                    loadingPosition="start"
                    startIcon={<></>}
                    variant="outlined"
                    type="button"
                    className="secondary-button"
                    name="cancel"
                    onClick={handleBack}>
                    Cancel
                  </LoadingButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </Box>
    </Box>
  );
}
